import { useState, useCallback, useRef } from 'react';
import { sparksService, type SparkSummary, type SparkDetail } from '@/services/sparks-service';

interface SparksCache {
  sparks: SparkSummary[];
  details: Map<string, SparkDetail>;
  hasLoaded: boolean;
  lastFetchTime: number;
}

// Global cache that persists across component re-mounts
let globalSparksCache: SparksCache = {
  sparks: [],
  details: new Map(),
  hasLoaded: false,
  lastFetchTime: 0
};

// Cache duration: 15 minutes (sparks data changes less frequently)
const CACHE_DURATION = 15 * 60 * 1000;
const DETAIL_CACHE_DURATION = 10 * 60 * 1000;

export const useSparksCache = () => {
  const [cache, setCache] = useState<SparksCache>(globalSparksCache);
  const [isLoading, setIsLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchPromiseRef = useRef<Map<string, Promise<any>>>(new Map());

  // Check if cache is still valid
  const isCacheValid = useCallback(() => {
    const now = Date.now();
    return globalSparksCache.hasLoaded && 
           (now - globalSparksCache.lastFetchTime) < CACHE_DURATION;
  }, []);

  // Check if detail cache is valid for a specific spark
  const isDetailCacheValid = useCallback((sparkId: string) => {
    const detail = globalSparksCache.details.get(sparkId);
    if (!detail) return false;
    
    const now = Date.now();
    // We store fetch time in a special property
    const fetchTime = (detail as any).__fetchTime || 0;
    return (now - fetchTime) < DETAIL_CACHE_DURATION;
  }, []);

  // Update global cache and local state
  const updateCache = useCallback((newCache: Partial<SparksCache>) => {
    globalSparksCache = { ...globalSparksCache, ...newCache };
    setCache(globalSparksCache);
  }, []);

  const fetchSparks = useCallback(async () => {
    // Return cached data if valid
    if (isCacheValid()) {
      console.log('✅ Using cached sparks data');
      return globalSparksCache.sparks;
    }

    // Prevent duplicate API calls
    const existingPromise = fetchPromiseRef.current.get('sparks-list');
    if (existingPromise) {
      console.log('⏳ Waiting for existing sparks fetch');
      return await existingPromise;
    }

    try {
      setIsLoading(true);
      setError(null);

      const fetchPromise = sparksService.getSparks();
      fetchPromiseRef.current.set('sparks-list', fetchPromise);
      console.log('🔄 Fetching sparks data');

      const response = await fetchPromise;
      const sparksData = response.data || [];

      updateCache({
        sparks: sparksData,
        hasLoaded: true,
        lastFetchTime: Date.now()
      });

      console.log(`✅ Cached ${sparksData.length} sparks`);
      return sparksData;
    } catch (error: any) {
      console.error('❌ Error fetching sparks:', error);
      setError('Failed to load sparks data');
      throw error;
    } finally {
      setIsLoading(false);
      fetchPromiseRef.current.delete('sparks-list');
    }
  }, [isCacheValid, updateCache]);

  const fetchSparkDetail = useCallback(async (sparkId: string): Promise<SparkDetail> => {
    // Return cached detail if valid
    if (isDetailCacheValid(sparkId)) {
      const cached = globalSparksCache.details.get(sparkId)!;
      console.log(`✅ Using cached detail for spark ${sparkId}`);
      return cached;
    }

    // Prevent duplicate API calls for the same detail
    const existingPromise = fetchPromiseRef.current.get(`detail-${sparkId}`);
    if (existingPromise) {
      console.log(`⏳ Waiting for existing detail fetch for ${sparkId}`);
      return await existingPromise;
    }

    try {
      setDetailLoading(true);
      setError(null);

      const fetchPromise = sparksService.getSparkById(sparkId);
      fetchPromiseRef.current.set(`detail-${sparkId}`, fetchPromise);
      console.log(`🔄 Fetching detail for spark ${sparkId}`);

      const response = await fetchPromise;
      const detailData = response.data;

      // Add fetch time for cache validation
      (detailData as any).__fetchTime = Date.now();

      // Update cache
      const newDetails = new Map(globalSparksCache.details);
      newDetails.set(sparkId, detailData);
      
      updateCache({
        details: newDetails
      });

      console.log(`✅ Cached detail for spark ${sparkId}`);
      return detailData;
    } catch (error: any) {
      console.error(`❌ Error fetching spark detail ${sparkId}:`, error);
      setError('Failed to load spark details');
      throw error;
    } finally {
      setDetailLoading(false);
      fetchPromiseRef.current.delete(`detail-${sparkId}`);
    }
  }, [isDetailCacheValid, updateCache]);

  const invalidateCache = useCallback(() => {
    updateCache({
      sparks: [],
      details: new Map(),
      hasLoaded: false,
      lastFetchTime: 0
    });
    console.log('🗑️ Invalidated sparks cache');
  }, [updateCache]);

  const invalidateDetailCache = useCallback((sparkId?: string) => {
    if (sparkId) {
      const newDetails = new Map(globalSparksCache.details);
      newDetails.delete(sparkId);
      updateCache({ details: newDetails });
      console.log(`🗑️ Invalidated detail cache for spark ${sparkId}`);
    } else {
      updateCache({ details: new Map() });
      console.log('🗑️ Invalidated all spark details cache');
    }
  }, [updateCache]);

  const getCachedDetail = useCallback((sparkId: string) => {
    return globalSparksCache.details.get(sparkId);
  }, []);

  return {
    sparks: cache.sparks,
    hasLoaded: cache.hasLoaded,
    isLoading,
    detailLoading,
    error,
    fetchSparks,
    fetchSparkDetail,
    invalidateCache,
    invalidateDetailCache,
    getCachedDetail,
    isCacheValid: isCacheValid(),
    isDetailCacheValid
  };
};
